import type { LandingPayload } from '@today/template-engine'
import { ContentBodyFormat, type OfferContactCard, type OfferLandingPayload } from '@today/types'
import { z } from 'zod'
import { localeTag } from '../product/product.schema.js'
import { brandIdSchema } from '../tenant/tenant-brand.schema.js'
import { OFFER_INTRODUCTION_MAX } from './offer.schema.js'

/**
 * Validation for an offer landing render request (TO-277 / TO-291).
 *
 * The render worker consumes queue messages and direct invocations, so nothing
 * upstream has already validated its input — this schema is the fail-fast
 * boundary that runs before the template is rendered and before anything is
 * written to S3. A request that fails it produces no artefact and no event.
 */

/**
 * Characters that render as nothing, so a field made only of them is visually
 * blank even though it has a non-zero length.
 *
 * `String.prototype.trim()` strips only Unicode `White_Space`, so none of these
 * survive-trimming cases are caught by a plain `.min(1)`:
 * - `\p{Cf}` — format characters: zero-width space/joiner, word joiner, BOM,
 *   soft hyphen, and the bidi controls (which can also reorder visible text).
 * - `\p{Mn}` — nonspacing combining marks, which have no width of their own.
 *   Stripping them cannot reject an accented name: a string keeps its base
 *   letters, and only a string that is *nothing but* marks ends up empty.
 * - `\p{Cc}` — the C0/C1 controls. `\s` covers only tab/newline/CR and friends,
 *   so NUL and DEL would otherwise survive as "content".
 * - The Hangul filler block, which is `Lo` but renders as blank space, and
 *   U+2800 BRAILLE PATTERN BLANK, which is `So` — the empty braille cell, the
 *   canonical glyph that renders as nothing.
 */
const BLANK_RENDERING = /[\s\p{Cf}\p{Mn}\p{Cc}\u115F\u1160\u2800\u3164\uFFA0]/gu

/**
 * Characters that hide or reorder visible text, rejected wherever they appear in
 * the value — not just when they make up the whole of it.
 *
 * The blank-rendering rule below asks "does this field render as nothing?", which
 * is a different question: `Ana\u202Emoc.knabelaf` has plenty of visible
 * characters and still renders as "Ana" followed by a right-to-left-reversed
 * "falebank.com" in a page carrying the bank's brand. Liquid's `outputEscape`
 * encodes `< > & " '` and nothing else, so no downstream control neutralises it
 * either — it has to be rejected here.
 *
 * - `\p{Cf}` — format characters: the bidi overrides, embeddings, isolates and
 *   marks (U+202A–U+202E, U+2066–U+2069, U+200E, U+200F) that reorder visible
 *   text, plus the ones that hide it (BOM, word joiner, soft hyphen, the
 *   interlinear annotation characters).
 * - `\p{Cc}` — the C0/C1 controls, which have no business in rendered copy.
 *
 * The two joiners are carved out: U+200C ZERO WIDTH NON-JOINER and U+200D ZERO
 * WIDTH JOINER carry meaning in Indic and Arabic scripts and inside emoji
 * sequences, so rejecting them would reject real names.
 */
const ALLOWED_JOINERS = /[\u200C\u200D]/gu

/**
 * A joiner outside its legitimate position: doubled, or at either edge of the
 * value.
 *
 * The carve-out above exempts U+200C/U+200D from every other rule, so leaving it
 * unbounded left a covert channel — 48 joiners in a row encode six ASCII
 * characters and render as nothing, riding along invisibly in an artefact that is
 * a legal record. A joiner does its job strictly BETWEEN two other characters
 * (`क‌ष`, `👨‍👩‍👧‍👦`, `ශ්‍රී`): it is never doubled and never leading or
 * trailing, so requiring that costs no legitimate text and bounds the channel to
 * one joiner per visible character.
 */
const MISPLACED_JOINER = /[\u200C\u200D]{2}|^[\u200C\u200D]|[\u200C\u200D]$/u
const UNSAFE_INVISIBLE = /[\p{Cc}\p{Cf}]/u

/**
 * Characters that render as nothing in EVERY context, so they are hidden text
 * wherever they appear rather than only when a field is made entirely of them:
 * U+034F COMBINING GRAPHEME JOINER (the canonical "invisible ink" code point),
 * the four Hangul fillers, and U+2800 BRAILLE PATTERN BLANK — the same
 * always-blank members of {@link BLANK_RENDERING}, now also barred mid-string.
 *
 * `\p{Mn}` as a whole deliberately stays out: a combining mark is invisible on
 * its own but carries meaning on its base, so the category covers Arabic
 * diacritics, Hebrew niqqud, Devanagari matras, Thai tone marks and accented
 * Latin. Rejecting it wholesale would reject real customer names.
 */
const ALWAYS_BLANK = /[\u034F\u115F\u1160\u2800\u3164\uFFA0]/u

/**
 * How many combining marks may sit on one base character.
 *
 * Measured, not guessed: across Indic (Devanagari, Bengali, Tamil, Telugu,
 * Kannada, Malayalam, Sinhala), South-East Asian (Thai, Lao, Khmer, Myanmar),
 * Semitic (Arabic harakat, Hebrew niqqud plus meteg), Latin (decomposed
 * Vietnamese, combining accents) and emoji samples, the deepest legitimate run is
 * two marks on one base. An arbitrarily deep stack is "Zalgo" text, which
 * overflows its line and smears over the copy around it. Three leaves a margin
 * over every script measured while still bounding the stack.
 */
const MAX_MARKS_PER_BASE = 3

/**
 * Every mark category, not a hand-picked subset.
 *
 * `\p{M}` is `Mn` (nonspacing) + `Mc` (spacing) + `Me` (enclosing), and Unicode
 * segmentation attaches all three to the base's grapheme cluster — so counting
 * only some of them both undercounts the stack AND lets an uncounted mark reset
 * the run. A Devanagari vowel sign is `Mc`, so `क` + 60 × `ी` is one smeared
 * cluster of ordinary same-script characters, with no joiner or cross-script
 * trick involved.
 */
const COMBINING_MARK = /\p{M}/u

/**
 * True when more than {@link MAX_MARKS_PER_BASE} combining marks stack on one base.
 *
 * Counts over the joiner-stripped value: a zero-width joiner is not a base, so
 * interleaving one between marks (`A◌́‍◌̂‍◌̃…`) would otherwise reset the run and let
 * the stack grow without limit through the carve-out.
 */
const hasOverlongMarkStack = (value: string): boolean => {
	let run = 0
	for (const char of value.replace(ALLOWED_JOINERS, '')) {
		if (!COMBINING_MARK.test(char)) {
			run = 0
			continue
		}
		run += 1
		if (run > MAX_MARKS_PER_BASE) return true
	}
	return false
}

/** Ordinary layout whitespace, which is legitimate in multi-line copy. */
const LAYOUT_WHITESPACE = /[\n\r\t]/gu

/**
 * True when `value` carries an invisible character that could hide or reorder the
 * visible text around it.
 *
 * @param allowLayoutWhitespace - Permit newlines and tabs, for multi-line copy.
 */
const hasUnsafeInvisible = (value: string, allowLayoutWhitespace = false): boolean => {
	const stripped = value.replace(ALLOWED_JOINERS, '')
	const controls = allowLayoutWhitespace ? stripped.replace(LAYOUT_WHITESPACE, '') : stripped
	return (
		UNSAFE_INVISIBLE.test(controls) ||
		ALWAYS_BLANK.test(value) ||
		MISPLACED_JOINER.test(value) ||
		hasOverlongMarkStack(value)
	)
}

/** Rejects a value carrying a text-reordering or text-hiding invisible character. */
const noUnsafeInvisible = <T extends z.ZodType<string>>(schema: T, allowLayoutWhitespace = false) =>
	schema.refine((value) => !hasUnsafeInvisible(value, allowLayoutWhitespace), {
		message:
			'must not contain bidirectional, control, always-blank or misplaced joiner characters, or an excessive mark stack',
	})

/** Payload text that must carry visible content: trimmed, non-blank, length-bounded. */
const boundedText = (max: number) =>
	noUnsafeInvisible(
		z
			.string()
			.trim()
			.min(1)
			.max(max)
			.refine((value) => value.replace(BLANK_RENDERING, '').length > 0, {
				message: 'must contain at least one visible character',
			}),
	)

/** Optional card text: same controls as {@link boundedText}; absence is expressed by omitting the key. */
const optionalText = (max: number) => boundedText(max).optional()

/**
 * The branch contact card (TO-425 REQ-005 / REQ-007): the unit's name, its
 * PRIMARY location's address and contact channels, and its manager's name.
 *
 * Strict at every level, so a stray key (a manager's email, say) is rejected
 * rather than silently carried into a customer-facing page. Every text field
 * carries the same visibility and invisible-character controls as the copy
 * fields above; `email` and `phone` are printed as text, never as links, so no
 * scheme restriction is needed.
 */
export const branchContactCardSchema = z.strictObject({
	unitName: boundedText(200),
	address: z
		.strictObject({
			line1: boundedText(200),
			line2: optionalText(200),
			postalCode: optionalText(16),
			city: boundedText(100),
			province: optionalText(100),
			countryCode: z.string().regex(/^[A-Z]{2}$/, 'countryCode must be ISO 3166-1 alpha-2 (uppercase)'),
		})
		.optional(),
	phone: optionalText(32),
	email: noUnsafeInvisible(z.email().max(254)).optional(),
	manager: z.strictObject({ name: boundedText(128) }).optional(),
})

/**
 * The sender's own contact card (TO-197 REQ-007 / REQ-008): their effective
 * display name, profile photo, phone and email. Strict, with the same text
 * controls as the branch card; the photo is an `<img src>`, so — like
 * `cta.href` — it is restricted to `https://` (escaping alone would still emit
 * a `javascript:` or `http:` URL into a customer-facing page). The name bound
 * is in UTF-16 units: 256 holds any 128-code-point display name.
 */
export const senderContactCardSchema: z.ZodType<OfferContactCard> = z.strictObject({
	name: boundedText(256),
	picture: noUnsafeInvisible(
		z
			.string()
			.max(2048)
			.regex(/^https:\/\/\S+$/, 'picture must be an https:// URL'),
	).optional(),
	phone: optionalText(32),
	email: noUnsafeInvisible(z.email().max(254)).optional(),
})

/** Content-service text code (`PRIVACY_GENERAL`), as content-authoring defines it. */
const TEXT_CODE = /^[A-Z][A-Z0-9_]{1,63}$/
const SHA256_HEX = /^[0-9a-f]{64}$/

/**
 * One resolved content text (TO-429 REQ-006). `body` is markdown-subset or plain
 * text — never HTML — and is NOT trimmed (markdown whitespace is significant);
 * it keeps the invisible-character guard with layout whitespace allowed, as the
 * former `legal_text` did. `localization_id` + `content_sha256` pin the exact
 * version shown so the snapshot of this payload records it.
 */
const legalTextFields = {
	code: z.string().regex(TEXT_CODE),
	title: boundedText(300),
	body: noUnsafeInvisible(z.string().min(1).max(100_000), true),
	body_format: z.enum(ContentBodyFormat),
	localization_id: z.uuid(),
	content_sha256: z.string().regex(SHA256_HEX),
}

const legalTextItemSchema = z.strictObject(legalTextFields)

/** A page-end text; `product_name` names the offer products a product text belongs to. */
const pageTextItemSchema = z.strictObject({ ...legalTextFields, product_name: boundedText(2000).optional() })

/**
 * The landing template's payload.
 *
 * Declared against the engine's own `LandingPayload`, so a change to that
 * interface breaks this file at compile time rather than drifting silently.
 *
 * `cta.href` is restricted to `https://` deliberately. The template emits
 * `<a href="{{ cta.href }}">`, and the Liquid engine's `outputEscape` stops a
 * value from breaking out of the attribute — but it would still happily emit
 * `href="javascript:alert(1)"` into a page served to a bank customer. Escaping
 * and scheme restriction are two different controls; this is the second one.
 *
 * The payload and its nested objects are strict: a stale producer still sending
 * the removed `product` / `legal_text` fields is refused, not silently stripped.
 */
export const landingPayloadSchema: z.ZodType<LandingPayload> = z.strictObject({
	customer: z.object({ name: boundedText(255) }),
	products: z
		.array(z.strictObject({ name: boundedText(255), legal_texts: z.array(legalTextItemSchema).max(50) }))
		.min(1)
		.max(50),
	cta: z.object({
		label: boundedText(120),
		href: noUnsafeInvisible(
			z
				.string()
				.min(1)
				.max(2048)
				.regex(/^https:\/\//, 'cta.href must be an https:// URL'),
		),
	}),
	legal: z.strictObject({ page_texts: z.array(pageTextItemSchema).max(100) }),
	branchContact: branchContactCardSchema.optional(),
	senderContact: senderContactCardSchema.optional(),
	/** The tenant's brand id frozen at offer create (TO-380): an id only, looked up in the catalogue. */
	brand_id: brandIdSchema.optional(),
	// TO-113: the offer's BCP-47 locale — the same tag rule the offer stores it
	// under. Optional so a message produced before the field existed still renders
	// (as `es`); anything that is not a tag is refused, never rendered.
	locale: localeTag.optional(),
	// TO-124: the offer's message, present only when non-blank — the same 2000
	// bound the offer stores it under. Rendered through the restricted markdown
	// renderer, never as HTML.
	introduction: z.string().min(1).max(OFFER_INTRODUCTION_MAX).optional(),
})

/**
 * A render request, shared by both trigger paths (REQ-007).
 *
 * `tenantId` is the owning tenant taken from the event envelope (never from a
 * caller-supplied `data` field, never from auth — the worker has no session).
 * `renderId` is optional: the event path derives it from the triggering
 * `eventId` so a redelivery resolves to the same immutable artefact key, and a
 * direct invocation may either supply one or let the worker mint it.
 */
export const renderRequestSchema = z.object({
	tenantId: z.uuid(),
	offerId: z.uuid(),
	renderId: z.uuid().optional(),
	/**
	 * The version this render produces (TO-464), stamped as `Ref: {offerId} · v{n}`.
	 * Absent on a request emitted before TO-464 — the worker stamps it v1 (such
	 * renders were never published, so that stamp is never seen on a sent document).
	 */
	renderVersion: z.number().int().min(1).optional(),
	payload: landingPayloadSchema,
})

export type RenderRequest = z.infer<typeof renderRequestSchema>

/**
 * What the render service actually acts on: a request whose `renderId` has been
 * resolved. Requiring it here rather than defending against its absence inside
 * the service keeps "every render is addressable" a type-level fact — the entry
 * path (the triggering `eventId`, or a freshly minted identifier) owns the
 * decision, and the service owns none of it.
 */
export const renderCommandSchema = renderRequestSchema.extend({ renderId: z.uuid() })

export type RenderCommand = z.infer<typeof renderCommandSchema>

/**
 * Compile-time proof that the event contract's structurally-declared payload
 * and the template engine's own `LandingPayload` describe the same shape.
 *
 * `@today/types` cannot import the engine (it is the leaf package the frontend
 * depends on), so the two declarations are written independently — this
 * assertion is what stops them drifting. If either side gains, loses, or
 * retypes a field, one of the two assignments below stops compiling. This file
 * is under `src/`, so `npm run typecheck` evaluates it on every build.
 */
const _enginePayloadMatchesEvent: OfferLandingPayload = {} as LandingPayload
const _eventPayloadMatchesEngine: LandingPayload = {} as OfferLandingPayload
void _enginePayloadMatchesEvent
void _eventPayloadMatchesEngine
